import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { CustomEventService } from '@shared/services/custom-event.service';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { OwnerEnum } from '@shared/enums/owner.enum';

import { validateFormGroup } from '@shared/utils/form.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PropertiesService } from "@core/services/properties.service";

@UntilDestroy()
@Component({
  selector: 'app-dashboard-settings-modal',
  templateUrl: './dashboard-settings-modal.component.html'
})
export class DashboardSettingsModalComponent implements OnInit, OnDestroy {

  @Input() mode: string;
  @Input() loading: boolean;
  @Input() fieldsLoading: boolean;
  @Input() data?: any = {};
  @Input() users?: any;
  @Input() teams?: any;
  @Input() permissions?: any;
  @Input() selectedTeams?: any[] = [];
  @Input() selectedUsers?: any[]  = [];
  @Input() selectedRole?: any;
  @Input() dashboards?: any;

  addForm: FormGroup;
  ownerEnum = OwnerEnum;

  // tab index
  selectedIndex = 0;

  constructor(
    private fb: FormBuilder,
    public propertiesService: PropertiesService,
    private customEventService: CustomEventService) { }

  ngOnInit(): void {
    this.loading = false;
    this.createForm();

    let privateDashboardTemp;

    this.addForm.get('privateDashboard').valueChanges
      .pipe(untilDestroyed(this)).subscribe(val => {
      if (val && privateDashboardTemp !== val) {
        privateDashboardTemp = val;
        this.customEventService.announceEvent(ActionTypesEnum.DASHBOARD_FORM_FIELDS);
      }
    });
  }

  createForm(): void {
    const { id, name, description, labels, copyFrom, privateDashboard } = this.data;

    this.addForm = this.fb.group({
      id: [id, []],
      name: [name, [Validators.required, Validators.maxLength(60)]],
      labels: [labels],
      copyFrom: [copyFrom, []],
      description: [description, []],
      teams: [null, []],
      users: [null, []],
      privateDashboard: [privateDashboard ? privateDashboard : false]
    });

    if (privateDashboard && !this.users) {
      this.customEventService.announceEvent(ActionTypesEnum.DASHBOARD_FORM_FIELDS);
    }
  }

  submitForm(): void {
    validateFormGroup(this.addForm);

    if (this.addForm.invalid) {
      return;
    }

    let formData = this.addForm.value;

    delete formData.teams;
    delete formData.users;

    if (formData.privateDashboard) {
      formData = {
        ...formData,
        selectedUsers: this.selectedUsers,
        selectedTeams: this.selectedTeams
      };
    }
    this.loading = true;
    this.customEventService.announceEvent(this.mode === 'add' ? ActionTypesEnum.ADD_DASHBOARD : ActionTypesEnum.UPDATE_DASHBOARD, formData);
  }

  resetForm(): void {
    this.createForm();
  }

  cancelForm(): void {
    this.customEventService.announceEvent(ActionTypesEnum.CLOSE_MODAL);
  }

  addTeams(): void {
    const mappedTeams = this.addForm.value.teams.map(item => {
      return {
        ...item,
        permission: this.selectedRole
      }
    });

    this.teams = this.teams.filter(item => !mappedTeams.some(mappedTeam => mappedTeam.id === item.id));

    this.selectedTeams = [
      ...this.selectedTeams,
      ...mappedTeams
    ];
    this.addForm.get('teams').setValue([]);
  }

  addUsers(): void {
    const mappedTeams = this.addForm.value.users.map(item => {
      return {
        ...item,
        permission: this.selectedRole
      }
    });

    this.users = this.users.filter(item => !mappedTeams.some(mappedTeam => mappedTeam.id === item.id));

    this.selectedUsers = [
      ...this.selectedUsers,
      ...mappedTeams
    ];
    this.addForm.get('users').setValue([]);
  }

  delete(item: any, type: string): void {
    if (type === this.ownerEnum.USER) {
      this.selectedUsers = this.selectedUsers.filter(user => user.id !== item.id);
      this.users = [
        ...this.users,
        item
      ];
    } else {
      this.selectedTeams = this.selectedTeams.filter(team => team.id !== item.id);
      this.teams = [
        ...this.teams,
        item
      ];
    }
  }

  changedRole({$event, item}): void {
    item.permission = this.permissions.find(permission => permission.key === $event);
  }

  tabChange($event): void {
    this.selectedIndex = $event;
  }

  dropdownChange(item: any): void  {
    this.selectedRole = item;
  }

  ngOnDestroy(): void {
    // prevent memory leak when component destroyed
  }
}
